/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getImageValidationErrorMessage } from '@bingads-webui-campaign/image-experience-utils';
import { useBrandKitService } from '@bingads-webui-campaign-react/brandkit-data-service';
import { useCopilotRepositoryContext } from '@bingads-webui/inline-copilot';
import { withCopilotDomainDataService } from '@bingads-webui-campaign-react/copilot-domain-data-service';
import { withRecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { withInstrumentation, InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { withLoggingContext } from '@bingads-webui-campaign-react/copilot-loggers';
import { EditVideoPageComponent } from './video-template-editor';
import { getGlobalLogoPickerStyles } from './styles';

export const VideoTemplateEditorWithAddImageWrapperImp = ({
  assetLibraryConfig,
  activity,
  i18n,
  logger,
  assetLibraryUserPreferences,
  assetLibraryAccountPreferences,
  ianaTimeZone,
  onApply,
  isOpen,
  setIsOpen,
  videoMetaData,
  cid,
  aid,
  oDataConfig,
  finalUrl,
  isCTV,
  preferenceService,
  serverContext,
  isDialog,
  imageAnimationEnabled,
  imageAnimationAvailable,
  permissions,
  loadClipchampPlayer,
  copilotDomainDataService,
  showVideoTemplateSummaryBarControl,
  ...rest
}) => {
  const [, setShowAddImagePanel] = useState(false);
  const [, setMinSizeOfImage] = useState([0, 0]);
  const [, setShowAddLogoPanel] = useState(false);
  const [brandKit, setBrandKit] = useState({});
  const [isLoadingBrandKit, setIsLoadingBrandKit] = useState(false);
  const [templateDetail, setTemplateDetail] = useState(null);
  const [rawAudioList, setRawAudioList] = useState([]);
  const instrumentation = useContext(InstrumentationContext);

  assetLibraryConfig.assetLibraryUserPreferences = assetLibraryUserPreferences;
  assetLibraryConfig.assetLibraryAccountPreferences = assetLibraryAccountPreferences;
  assetLibraryConfig.logger = logger;
  assetLibraryConfig.ianaTimeZone = ianaTimeZone;
  assetLibraryConfig.onImageAddError = (e) => {
    const errorCode = e && e.errors && e.errors[0] && e.errors[0].Code;
    const errorMessage = getImageValidationErrorMessage({ message: errorCode }, i18n);
    return errorMessage;
  };

  const copilotDataService = useCopilotRepositoryContext();
  const { brandKitDataService } = useBrandKitService();
  const { getBrandKits } = brandKitDataService;

  // Fetch BrandKits
  const fetchBrandKits = useCallback(async () => {
    if (getBrandKits) {
      setIsLoadingBrandKit(true);
      const { response } = await getBrandKits();
      if (Array.isArray(response) && response.length > 0) {
        setBrandKit(response[0]);
      }
      setIsLoadingBrandKit(false);
    }
  }, [getBrandKits]);

  const fetchTemplateDetails = useCallback(async () => {
    if (copilotDomainDataService && videoMetaData && videoMetaData.template.templateId) {
      try {
        const templateFilter = {
          TemplateIds: [videoMetaData.template.templateId],
        };
        const { response } = await copilotDomainDataService.getVideoTemplates(templateFilter, i18n, permissions);
        setTemplateDetail((response && response.Templates && response.Templates.length > 0 ? response.Templates[0] : null));
      } catch (error) {
        setTemplateDetail(null);
      }
    }
  }, [copilotDomainDataService, videoMetaData, i18n]);

  const fetchAudioList = useCallback(async () => {
    const { response, error } = await copilotDomainDataService.getAudioList();
    if (error) {
      setRawAudioList([]);
      return;
    }

    setRawAudioList(response);
  }, [copilotDomainDataService]);

  useEffect(() => {
    fetchAudioList();
  }, [fetchAudioList]);

  useEffect(() => {
    fetchBrandKits();
  }, [fetchBrandKits]);

  useEffect(() => {
    fetchTemplateDetails();
  }, [fetchTemplateDetails]);

  useEffect(() => {
    if (activity && activity.signal) {
      const message = `[VideoAdsEditor] - Edit Video - Open - TemplateId:${videoMetaData.template.templateId}, AdStudio:${showVideoTemplateSummaryBarControl}`;
      activity.signal(message);
    }
  }, [activity, videoMetaData, showVideoTemplateSummaryBarControl]);

  return (
    <div className="video-editor-with-add-image-wrapper">
      <EditVideoPageComponent
        i18n={i18n}
        activity={activity}
        onApply={onApply}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        videoMetaData={videoMetaData}
        cid={cid}
        aid={aid}
        oDataConfig={oDataConfig}
        finalUrl={finalUrl}
        copilotDataService={copilotDataService}
        logger={logger}
        onChangeImage={(minSize) => {
          setShowAddImagePanel(true);
          setMinSizeOfImage(minSize);
        }}
        assetLibraryConfig={assetLibraryConfig}
        onChangeLogo={() => setShowAddLogoPanel(true)}
        isCTV={isCTV}
        preferenceService={preferenceService}
        serverContext={serverContext}
        perfMarker={rest.perfMarker}
        isDialog={isDialog}
        imageAnimationEnabled={imageAnimationEnabled}
        imageAnimationAvailable={imageAnimationAvailable}
        permissions={permissions}
        loadClipchampPlayer={loadClipchampPlayer}
        brandKit={brandKit}
        isLoadingBrandKit={isLoadingBrandKit}
        templateDetail={templateDetail}
        copilotDomainDataService={copilotDomainDataService}
        instrumentation={instrumentation}
        showVideoTemplateSummaryBarControl={showVideoTemplateSummaryBarControl}
        rawAudioList={rawAudioList}
      />
    </div>
  );
};

VideoTemplateEditorWithAddImageWrapperImp.propTypes = {
  assetLibraryConfig: PropTypes.shape({
    activityLogger: PropTypes.shape({}),
    assetLibraryUserPreferences: PropTypes.shape({}),
    assetLibraryAccountPreferences: PropTypes.shape({}),
    ianaTimeZone: PropTypes.string,
    logger: PropTypes.shape({}),
    onImageAddError: PropTypes.func,
  }).isRequired,
  copilotDataService: PropTypes.shape({}).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  initialView: PropTypes.string.isRequired,
  isAssetLibraryAIGCMVPEnabled: PropTypes.bool.isRequired,
  isLiteLandingPageEnabled: PropTypes.bool.isRequired,
  logger: PropTypes.shape({}).isRequired,
  assetLibraryUserPreferences: PropTypes.shape({}).isRequired,
  assetLibraryAccountPreferences: PropTypes.shape({}).isRequired,
  ianaTimeZone: PropTypes.string.isRequired,
  onApply: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  videoMetaData: PropTypes.shape({
    images: PropTypes.arrayOf(PropTypes.string),
    text: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  cid: PropTypes.string.isRequired,
  aid: PropTypes.string.isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  finalUrl: PropTypes.string.isRequired,
  permissions: PropTypes.shape({
    IsSocialMediaImagesEnabled: PropTypes.bool,
  }).isRequired,
  isCTV: PropTypes.bool,
  preferenceService: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  isDialog: PropTypes.bool,
  imageAnimationEnabled: PropTypes.bool,
  imageAnimationAvailable: PropTypes.bool,
  loadClipchampPlayer: PropTypes.func.isRequired,
};

VideoTemplateEditorWithAddImageWrapperImp.defaultProps = {
  isCTV: false,
  isDialog: false,
  imageAnimationEnabled: false,
  imageAnimationAvailable: false,
};

export const VideoTemplateEditorWithAddImageWrapper = withInstrumentation(withLoggingContext(withRecommendationContext(withCopilotDomainDataService(withDefaultStyles(VideoTemplateEditorWithAddImageWrapperImp, getGlobalLogoPickerStyles)))));
